import React from 'react';
import { Button, Checkbox, colors } from '@insurancemenu/imenu-components-lib';
import { Reload } from '@insurancemenu/imenu-components-lib/dist/icons';
import { useGeneratePDFStore } from 'store/pages/rfq/rfq-template/generate-pdf/pageStore';
import PlanAttributeBlock from 'components/common/rfq/PlanAttributeBlock';
import AttributeCoverageTag from 'components/common/rfq/AttributeCoveredTag';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import FormsSpinner from 'components/common/loaders/FormSpinner';
import { createStyles } from './styles';
import {
  ATTRIBUTES_SELECTION_LIST_TEST_ID,
  GENERATE_PDF_BTN_TEST_ID,
  GENERATE_PDF_PAGE_LOADING_TEST_ID,
  SELECT_AND_GENERATE_BLOCK_GUIDELINES_TEST_ID,
  SELECT_ATTRIBUTE_CHECKBOX_TEST_ID,
} from '../../constants';

interface RFQAttributesSelectionListProps {
  showAllAttributes: boolean;
}

const RFQAttributesSelectionList = observer(({ showAllAttributes }: RFQAttributesSelectionListProps): JSX.Element => {
  const classes = createStyles();
  const store = useGeneratePDFStore();

  const {
    isLoading,
    isCompleteStepLoading,
    planAttributesMap,
    isPdfGenerated,
    isTemplateInProgress,
    totalSelectedAttributesForPdf,
    toggleAttributeSelection,
    generatePdf,
  } = store;

  const onGenerate = async (): Promise<void> => {
    const downloadLink = await generatePdf();
    if (downloadLink) window.open(downloadLink, '_blank');
  };

  return (
    <>
      <div className={classes.selectionPanel}>
        <FormsSpinner display={isLoading} semiTransparentBg data-test-id={GENERATE_PDF_PAGE_LOADING_TEST_ID} />
      </div>
      {/* Show attributes list and generate button  only if showAllAttributes is set to true using styles */}
      {!showAllAttributes && (
        <p className={classes.sectionGuideline} data-test-id={SELECT_AND_GENERATE_BLOCK_GUIDELINES_TEST_ID}>
          Click the button to access the content.
        </p>
      )}
      <div
        className={clsx(
          classes.attributesSection,
          !showAllAttributes && classes.closedAttributesSection,
          !isTemplateInProgress && classes.readyTemplateAttributesSection,
        )}
      >
        <div className={classes.attributesList} data-test-id={ATTRIBUTES_SELECTION_LIST_TEST_ID}>
          {Object.entries(planAttributesMap).map(
            ([attrName, { formValues, engValues, isCovered, type, maxWaiverMessage, status }]) => (
              <div key={attrName} className={classes.attributeSelectionBlock}>
                <div className={classes.selectionStatusCheckbox}>
                  <Checkbox
                    label=""
                    name={attrName}
                    onChange={(): void => {
                      toggleAttributeSelection(attrName);
                    }}
                    checked={status}
                    disabled={!isTemplateInProgress}
                    highlightColor={colors.blue[600]}
                    data-test-id={SELECT_ATTRIBUTE_CHECKBOX_TEST_ID}
                  />
                </div>
                <PlanAttributeBlock
                  key={attrName}
                  label={attrName}
                  headerElement={<AttributeCoverageTag isAttributeCovered={isCovered} />}
                  planAttribute={formValues}
                  engValues={engValues}
                  planType={type}
                  maxWaiverMessage={maxWaiverMessage}
                  isAttributeOpenByDefault
                />
              </div>
            ),
          )}
        </div>
        {isTemplateInProgress && (
          <Button
            color="primary"
            leftIconProp={<Reload />}
            size="large"
            iconSize="2rem"
            onClick={onGenerate}
            data-test-id={GENERATE_PDF_BTN_TEST_ID}
            disabled={totalSelectedAttributesForPdf === 0 || isLoading || isCompleteStepLoading}
          >
            {isPdfGenerated ? 'Re-Generate PDF' : 'Generate PDF'}
          </Button>
        )}
      </div>
    </>
  );
});

export default RFQAttributesSelectionList;
